import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { generateRestoSlug } from "../utils/RestoSlugUtils";
import LoadingIndicator from "./LoadingIndicator";
import { buildApiUrl } from "../utils/ApiConfig";
import { CloseIcon } from "./Icon";
import { sidebarStateManager } from "../utils/SidebarStateManager";
import { useSidebarState } from "../hooks/useSidebarState";
import styles from "./RestaurantDetailsSidebar.module.css";
import axios from "axios";

interface ProvinceStats {
  name: string;
  nameNl: string;
  nameFr: string;
  restaurantCount: number;
  avgScore: number;
  reportCount: number;
  topRestaurants: { id: number; name: string; inspectionForks: number; address: string }[];
  worstRestaurants: { id: number; name: string; inspectionForks: number; address: string }[];
}

interface ProvinceStatsContentProps {
  provinceSlug?: string;
  initialData?: ProvinceStats;
}

const getScoreEmoji = (score: number) => {
  if (score === 5) return "😍";
  if (score === 4) return "😃";
  if (score === 3) return "🙂";
  if (score === 2) return "🤮";
  if (score === 1) return "💀";
  return "❓";
};

const ProvinceStatsContent: React.FC<ProvinceStatsContentProps> = ({ provinceSlug, initialData }) => {
  const { t, i18n } = useTranslation();
  const { closeSidebar } = useSidebarState(sidebarStateManager);
  const [stats, setStats] = useState<ProvinceStats | null>(initialData || null);
  const [loading, setLoading] = useState(!initialData);

  useEffect(() => {
    if (initialData || !provinceSlug) return;
    const fetchStats = async () => {
      setLoading(true);
      try {
        const url = buildApiUrl(`api/province/${encodeURIComponent(provinceSlug)}/stats`);
        const response = await axios.get(url, { timeout: 10000 });
        setStats(response.data);
      } catch {
        // Failed to fetch
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [provinceSlug, initialData]);

  const lang = i18n.language || 'en';
  const isNL = lang === 'nl';
  const isFR = lang === 'fr';

  const provinceName = stats
    ? (isFR ? stats.nameFr : isNL ? stats.nameNl : stats.name)
    : provinceSlug?.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || '';

  // Set dynamic page title when stats are available
  useEffect(() => {
    if (!stats) return;
    const name = isFR ? stats.nameFr : isNL ? stats.nameNl : stats.name;
    document.title = isNL
      ? `Restaurant Hygiëne in ${name} - Voedselveiligheid | Kauwen of Spauwen`
      : isFR
        ? `Hygiène des Restaurants - ${name} | Kauwen of Spauwen`
        : `Restaurant Food Safety in ${name} - Hygiene Scores | Kauwen of Spauwen`;
  }, [stats, lang]);

  const getRestoUrl = (id: number, name: string) => {
    const slug = generateRestoSlug(name, id);
    return `/${lang}/restaurant/${slug}`;
  };

  const handleRestaurantClick = (e: React.MouseEvent, id: number, name: string) => {
    e.preventDefault();
    window.dispatchEvent(new CustomEvent('restaurantSelected', {
      detail: { restaurantId: id.toString(), openSidebar: true }
    }));
    window.history.pushState({}, '', getRestoUrl(id, name));
  };

  return (
    <div className="flex flex-col h-full">
      <div className={`flex-grow overflow-y-auto overflow-x-hidden ${styles.customScrollbar}`}>
        <div className="pl-4 pr-2 pt-4 pb-16">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">
              {provinceName}
            </h3>
            <button
              onClick={closeSidebar}
              className="p-2 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg transition-all duration-200"
              aria-label="Close"
            >
              <CloseIcon />
            </button>
          </div>

          {loading && (
            <div className="flex items-center justify-center py-12">
              <LoadingIndicator isLoading={true} />
            </div>
          )}

          {!loading && !stats && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400 font-medium mb-2">
                {t('city.noData', 'No data available yet')}
              </p>
            </div>
          )}

          {!loading && stats && (
            <>
              <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700 text-center">
                  <div className="text-xl font-bold text-gray-800 dark:text-gray-100">{stats.restaurantCount}</div>
                  <div className="text-[10px] text-gray-500 dark:text-gray-400">{t('city.restaurants', 'Restaurants')}</div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700 text-center">
                  <div className="text-xl font-bold text-gray-800 dark:text-gray-100">{stats.avgScore}/5</div>
                  <div className="text-[10px] text-gray-500 dark:text-gray-400">{t('city.avgScore', 'Avg Score')}</div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700 text-center">
                  <div className="text-xl font-bold text-gray-800 dark:text-gray-100">{stats.reportCount}</div>
                  <div className="text-[10px] text-gray-500 dark:text-gray-400">{t('city.reports', 'Reports')}</div>
                </div>
              </div>

              {/* Data-driven prose for SEO uniqueness */}
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 leading-relaxed">
                {t('province.prose.overview', '{{province}} has {{count}} inspected restaurants with an average hygiene score of {{avg}}/5.', {
                  province: provinceName,
                  count: stats.restaurantCount,
                  avg: stats.avgScore > 0 ? stats.avgScore.toFixed(1) : '—'
                })}
                {' '}
                {stats.reportCount > 0 && t('province.prose.reports', '{{count}} food safety reports have been filed in {{province}}.', {
                  count: stats.reportCount,
                  province: provinceName
                })}
                {stats.topRestaurants.length > 0 && (' ' + t('province.prose.topPick', 'Top rated: {{name}}.', {
                  name: stats.topRestaurants[0].name.split('|')[0].trim()
                }))}
              </p>

              {stats.topRestaurants.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">{t('city.bestRestaurants', 'Best Rated')}</h4>
                  <div className="space-y-1">
                    {stats.topRestaurants.map((resto) => (
                      <a key={resto.id} href={getRestoUrl(resto.id, resto.name)} onClick={(e) => handleRestaurantClick(e, resto.id, resto.name)}
                        className="bg-white dark:bg-gray-800/70 rounded-lg p-3 border border-gray-200 dark:border-gray-700/60 cursor-pointer hover:shadow-md transition-shadow flex justify-between items-center no-underline">
                        <div className="min-w-0 flex-1">
                          <h5 className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{resto.name}</h5>
                        </div>
                        <span className="text-lg ml-2">{getScoreEmoji(resto.inspectionForks)}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {stats.worstRestaurants.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">{t('city.worstRestaurants', 'Lowest Rated')}</h4>
                  <div className="space-y-1">
                    {stats.worstRestaurants.map((resto) => (
                      <a key={resto.id} href={getRestoUrl(resto.id, resto.name)} onClick={(e) => handleRestaurantClick(e, resto.id, resto.name)}
                        className="bg-white dark:bg-gray-800/70 rounded-lg p-3 border border-gray-200 dark:border-gray-700/60 cursor-pointer hover:shadow-md transition-shadow flex justify-between items-center no-underline">
                        <div className="min-w-0 flex-1">
                          <h5 className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{resto.name}</h5>
                        </div>
                        <span className="text-lg ml-2">{getScoreEmoji(resto.inspectionForks)}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProvinceStatsContent;
